import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { formatShiftType, formatConfidence, getSystemColorClass, capitalizeText } from '../../utils/displayUtils';
import { createShareableEvidenceItemUrl } from '../../utils/urlUtils';
import { TEMPORAL_COLORS } from '../../utils/timelineUtils';
import { track } from '@vercel/analytics/react';
import { InlineTagSelector } from '../Tags/InlineTagSelector';
import { TagDisplay } from '../Tags/TagDisplay';
import { useEvidenceTags } from '../../hooks/useSupabase';
import { EvidenceTag } from '../../types/types';

interface EvidenceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EvidenceDetailModal: React.FC<EvidenceDetailModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const { selectedEvidence, setSelectedEvidence, filteredEvidence } = useData();
  const navigate = useNavigate();
  const [shareUrl, setShareUrl] = useState<string>('');
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [isContextExpanded, setIsContextExpanded] = useState(true);
  const [isSourceExpanded, setIsSourceExpanded] = useState(false);
  const [isDataQualityExpanded, setIsDataQualityExpanded] = useState(false);
  const [showStatusTooltip, setShowStatusTooltip] = useState(false);
  const [showImplementationTooltip, setShowImplementationTooltip] = useState(false);
  const [currentTags, setCurrentTags] = useState<EvidenceTag[]>([]);
  const { getTagsForEvidence } = useEvidenceTags();
  
  // Close modal with escape key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscKey);
    
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Generate share URL and fetch tags when evidence is selected
  useEffect(() => {
    if (selectedEvidence) {
      setShareUrl(createShareableEvidenceItemUrl(selectedEvidence.id.toString()));
      
      // Fetch tags for the selected evidence
      getTagsForEvidence(selectedEvidence.evidence_id)
        .then(tags => {
          setCurrentTags(tags || []);
        })
        .catch(error => {
          console.error('Error fetching tags:', error);
          setCurrentTags([]);
        });
    }
  }, [selectedEvidence, getTagsForEvidence]);

  // Track evidence view event when modal is opened with evidence
  useEffect(() => {
    if (isOpen && selectedEvidence) {
      track('view_evidence', {
        evidence_id: selectedEvidence.id.toString(),
        shift_type: selectedEvidence.shift_type,
        confidence: selectedEvidence.confidence,
        entity_count: selectedEvidence.entities.length,
        has_context: !!selectedEvidence.context
      });
    }
  }, [isOpen, selectedEvidence]);

  // If modal is closed or no evidence selected, don't render
  if (!isOpen || !selectedEvidence) {
    return null;
  }

  const getPeriodColorStyle = (periodLabel: string) => {
    const color = TEMPORAL_COLORS.periods[periodLabel as keyof typeof TEMPORAL_COLORS.periods];
    if (color) {
      return { backgroundColor: color + '20', color: color };
    }
    return { backgroundColor: '#9333ea20', color: '#9333ea' };
  };
  
  // Split systems for display
  const systems = selectedEvidence.context?.systems_affected
    ? selectedEvidence.context.systems_affected
        .split(/[;,]+/)
        .map(s => s.trim())
        .filter(s => s)
    : [];
  
  // Get index of current evidence to enable navigation
  const currentIndex = filteredEvidence.findIndex(e => e.id === selectedEvidence.id);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < filteredEvidence.length - 1;
  
  const handlePrevious = () => {
    if (hasPrevious) {
      const newEvidence = filteredEvidence[currentIndex - 1];
      setSelectedEvidence(newEvidence);
      
      // Fetch tags for the new evidence
      getTagsForEvidence(newEvidence.evidence_id)
        .then(tags => {
          setCurrentTags(tags || []);
        })
        .catch(error => {
          console.error('Error fetching tags:', error);
          setCurrentTags([]);
        });
      
      // Track navigation to previous evidence
      track('navigate_evidence', {
        direction: 'previous',
        from_index: currentIndex,
        to_index: currentIndex - 1
      });
    }
  };
  
  const handleNext = () => {
    if (hasNext) {
      const newEvidence = filteredEvidence[currentIndex + 1];
      setSelectedEvidence(newEvidence);
      
      // Fetch tags for the new evidence
      getTagsForEvidence(newEvidence.evidence_id)
        .then(tags => {
          setCurrentTags(tags || []);
        })
        .catch(error => {
          console.error('Error fetching tags:', error);
          setCurrentTags([]);
        });
      
      // Track navigation to next evidence
      track('navigate_evidence', {
        direction: 'next',
        from_index: currentIndex,
        to_index: currentIndex + 1
      });
    }
  };
  
  const handleEntityClick = (entity: string) => {
    // Track entity click
    track('click_entity', {
      entity: entity,
      from_evidence_id: selectedEvidence.id.toString()
    });
    
    // Navigate to the entity explorer with the selected entity
    onClose(); // Close modal first
    navigate(`/entities?entity=${encodeURIComponent(entity)}`);
  };

  const handleCopyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShowShareTooltip(true);
      setTimeout(() => setShowShareTooltip(false), 3000);
      
      // Track share event
      track('share_evidence_item', {
        method: 'copy_link',
        evidence_id: selectedEvidence.id.toString()
      });
    } catch (err) {
      console.error('Failed to copy link: ', err);
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`Evidence Item: ${selectedEvidence.description.substring(0, 50)}...`);
    const body = encodeURIComponent(`Check out this evidence item: ${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
    
    // Track email share event
    track('share_evidence_item', {
      method: 'email',
      evidence_id: selectedEvidence.id.toString()
    });
  };
  
  return (
    <div
      className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Modal header */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Evidence Details</h2>
          <div className="flex space-x-2">
            <div className="relative">
              <button 
                onClick={handleCopyShareUrl}
                className="px-3 py-1 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium flex items-center"
                aria-label="Share evidence"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
              </button>
              {showShareTooltip && (
                <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg" role="status" aria-live="polite">
                  Link copied to clipboard!
                </div>
              )}
            </div>
            <button 
              onClick={handleEmailShare}
              className="px-3 py-1 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium flex items-center"
              aria-label="Share by email"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 text-sm font-medium flex items-center"
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Close
            </button>
          </div>
        </div>
        
        {/* Modal body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="mb-4">
            <h3 className="text-lg font-semibold">{selectedEvidence.description}</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="border-b border-gray-100 pb-3">
              <p className="text-sm text-gray-500 mb-1">Shift Type</p>
              <p className="text-blue-600 font-medium">{formatShiftType(selectedEvidence.shift_type)}</p>
            </div>
            <div className="border-b border-gray-100 pb-3">
              <p className="text-sm text-gray-500 mb-1">Confidence</p>
              <p className="text-blue-600 font-medium">{formatConfidence(selectedEvidence.confidence)}</p>
            </div>
            {selectedEvidence.reporting_period?.label && (
              <div className="border-b border-gray-100 pb-3">
                <p className="text-sm text-gray-500 mb-1">Reporting Period</p>
                <div
                  className="inline-block rounded-full px-3 py-1 text-sm font-medium"
                  style={getPeriodColorStyle(selectedEvidence.reporting_period.label)}
                >
                  {selectedEvidence.reporting_period.label}
                </div>
              </div>
            )}
            {systems.length > 0 && (
              <div className="border-b border-gray-100 pb-3">
                <p className="text-sm text-gray-500 mb-1">Systems Affected</p>
                <div className="flex flex-wrap gap-1">
                  {systems.map((system, idx) => (
                    <div 
                      key={idx}
                      className={`inline-block rounded-full px-2 py-0.5 text-xs ${getSystemColorClass(system)}`}
                    >
                      {system}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {selectedEvidence.temporal_status && (
              <div className="relative border-b border-gray-100 pb-3">
                <p className="text-sm text-gray-500 mb-1">
                  Status{' '}
                  <button
                    className="inline-flex items-center align-middle"
                    onClick={() => setShowStatusTooltip(!showStatusTooltip)}
                    onBlur={() => setShowStatusTooltip(false)}
                    aria-label="Show status descriptions"
                  >
                    <svg
                      className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                </p>
                {showStatusTooltip && (
                  <div className="absolute z-20 left-0 top-5 bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-64">
                    <div className="text-sm">
                      <p className="font-semibold text-gray-900 mb-2">Timeline Status</p>
                      <p className="text-gray-700 mb-2">Indicates where this evidence sits in the project timeline:</p>
                      <ul className="space-y-1">
                        <li className="flex items-start">
                          <span className="text-gray-500 mr-2">•</span>
                          <span><strong className="text-gray-800">Planned:</strong> Future activity</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-gray-500 mr-2">•</span>
                          <span><strong className="text-gray-800">In Progress:</strong> Currently active</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-gray-500 mr-2">•</span>
                          <span><strong className="text-gray-800">Completed:</strong> Finished activity</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                <div className="inline-block bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs font-medium">
                  {capitalizeText(selectedEvidence.temporal_status)}
                </div>
              </div>
            )}
            {selectedEvidence.implementation_stage?.status && (
              <div className="relative">
                <p className="text-sm text-gray-500 mb-1">
                  Implementation Stage{' '}
                  <button
                    className="inline-flex items-center align-middle"
                    onClick={() => setShowImplementationTooltip(!showImplementationTooltip)}
                    onBlur={() => setShowImplementationTooltip(false)}
                    aria-label="Show implementation stage descriptions"
                  >
                    <svg
                      className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                </p>
                {showImplementationTooltip && (
                  <div className="absolute z-20 left-0 top-5 bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-72">
                    <div className="text-sm">
                      <p className="font-semibold text-gray-900 mb-2">Implementation Maturity</p>
                      <p className="text-gray-700 mb-2">Shows how embedded this initiative is within the system:</p>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <div className="w-20 text-xs font-medium text-gray-600">Planning</div>
                          <div className="flex-1 h-2 bg-gray-200 rounded-full relative">
                            <div className="absolute left-0 w-1/5 h-full bg-gradient-to-r from-red-400 to-orange-400 rounded-full"></div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-20 text-xs font-medium text-gray-600">Developing</div>
                          <div className="flex-1 h-2 bg-gray-200 rounded-full relative">
                            <div className="absolute left-0 w-2/5 h-full bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full"></div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-20 text-xs font-medium text-gray-600">Piloting</div>
                          <div className="flex-1 h-2 bg-gray-200 rounded-full relative">
                            <div className="absolute left-0 w-3/5 h-full bg-gradient-to-r from-yellow-400 to-green-400 rounded-full"></div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-20 text-xs font-medium text-gray-600">Implementing</div>
                          <div className="flex-1 h-2 bg-gray-200 rounded-full relative">
                            <div className="absolute left-0 w-4/5 h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full"></div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-20 text-xs font-medium text-gray-600">Embedded</div>
                          <div className="flex-1 h-2 bg-gray-200 rounded-full relative">
                            <div className="absolute left-0 w-full h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="inline-block bg-green-100 text-green-800 rounded-full px-2 py-0.5 text-xs font-medium">
                  {capitalizeText(selectedEvidence.implementation_stage.status)}
                </div>
              </div>
            )}
          </div>
          
          {/* Related Entities - Above tags section */}
          {selectedEvidence.entities.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm text-gray-500 mb-2">Related Entities</h4>
              <div className="flex flex-wrap gap-2">
                {selectedEvidence.entities.map((entity, index) => (
                  <button
                    key={index}
                    onClick={() => handleEntityClick(entity)}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm inline-flex items-center"
                    title="View in Stakeholders"
                  >
                    {entity}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Tags Section - Always visible, inline editing */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Tags</h3>
            <InlineTagSelector
              evidenceId={selectedEvidence.evidence_id}
              currentTags={currentTags}
              allowEdit={true}
              onTagsChange={(tagIds, tags) => {
                // Update local state to reflect the changes immediately
                console.log('Tags updated:', tagIds, tags);
                if (tags) {
                  setCurrentTags(tags);
                  // Also update the selected evidence to keep it in sync
                  if (selectedEvidence) {
                    setSelectedEvidence({
                      ...selectedEvidence,
                      tags: tags
                    });
                  }
                }
              }}
            />
            {currentTags.length === 0 && (
              <p className="text-xs text-gray-400 italic mt-2">Categorise this evidence by adding impact stream tags</p>
            )}
          </div>
          
          
          {/* Context - Main Body Content with Accordion */}
          {selectedEvidence.context && (
            <div className="mb-6 border border-gray-200 rounded-lg">
              <button
                onClick={() => setIsContextExpanded(!isContextExpanded)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="text-lg font-semibold">Context</h3>
                <svg 
                  className={`h-5 w-5 text-gray-500 transform transition-transform ${isContextExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isContextExpanded && (
                <div className="px-4 pb-4">
                  {/* Source Document - First in Context */}
                  {selectedEvidence.sharepoint_link && (
                    <div className="mb-3 pt-2">
                      <span className="text-sm text-gray-500">Source Document: </span>
                      <span className="text-sm text-gray-700">
                        {selectedEvidence.sharepoint_link}
                      </span>
                    </div>
                  )}
                  
                  {selectedEvidence.context.background && (
                    <div className="mb-4">
                      <h4 className="text-sm text-gray-500 mb-2">Background</h4>
                      <p className="text-gray-800 bg-gray-50 p-3 rounded border border-gray-100">
                        {selectedEvidence.context.background}
                      </p>
                    </div>
                  )}
                  
                  {selectedEvidence.context.immediate_impact && (
                    <div className="mb-4">
                      <h4 className="text-sm text-gray-500 mb-2">Immediate Impact</h4>
                      <p className="text-gray-800 bg-gray-50 p-3 rounded border border-gray-100">
                        {selectedEvidence.context.immediate_impact}
                      </p>
                    </div>
                  )}
                  
                  {selectedEvidence.context.future_implications && (
                    <div className="mb-4">
                      <h4 className="text-sm text-gray-500 mb-2">Future Implications</h4>
                      <p className="text-gray-800 bg-gray-50 p-3 rounded border border-gray-100">
                        {selectedEvidence.context.future_implications}
                      </p>
                    </div>
                  )}
                  
                  {selectedEvidence.context.stakeholders && selectedEvidence.context.stakeholders.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm text-gray-500 mb-2">Stakeholders</h4>
                      <ul className="list-disc list-inside text-gray-800 bg-gray-50 p-3 rounded border border-gray-100">
                        {selectedEvidence.context.stakeholders.map((stakeholder, idx) => (
                          <li key={idx}>{stakeholder}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {selectedEvidence.implementation_stage?.progress_description && (
                    <div className="mb-4">
                      <h4 className="text-sm text-gray-500 mb-2">Implementation Progress</h4>
                      <p className="text-gray-800 bg-gray-50 p-3 rounded border border-gray-100">
                        {selectedEvidence.implementation_stage.progress_description}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          
          {/* Supporting Detail with Accordion */}
          {(selectedEvidence.source_reference || 
            selectedEvidence.sharepoint_link || 
            selectedEvidence.supporting_details?.quotes?.length > 0 ||
            selectedEvidence.supporting_details?.metrics?.length > 0 ||
            selectedEvidence.supporting_details?.timeline ||
            selectedEvidence.supporting_details?.conditions ||
            selectedEvidence.supporting_details?.related_initiatives?.length > 0) && (
            <div className="mb-6 border border-gray-200 rounded-lg">
              <button
                onClick={() => setIsSourceExpanded(!isSourceExpanded)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h4 className="font-semibold text-gray-700">Supporting Detail</h4>
                <svg 
                  className={`h-5 w-5 text-gray-500 transform transition-transform ${isSourceExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isSourceExpanded && (
                <div className="px-4 pb-4 pt-2">
                  {/* Section/Source - First (was Second) */}
                  {selectedEvidence.source_reference?.section && (
                    <div className="mb-3">
                      <span className="text-sm text-gray-500">{selectedEvidence.sharepoint_link ? 'Section' : 'Source'}: </span>
                      <span className="text-gray-700">{selectedEvidence.source_reference.section}</span>
                    </div>
                  )}
                  
                  {/* Key Quote - Third */}
                  {selectedEvidence.source_reference?.key_quote && (
                    <div className="mb-3">
                      <span className="text-sm text-gray-500">Key Quote: </span>
                      <p className="text-gray-700 mt-1 italic bg-gray-50 p-2 rounded border-l-4 border-blue-400">
                        "{selectedEvidence.source_reference.key_quote}"
                      </p>
                    </div>
                  )}
                  
                  {/* Other Quotes from Supporting Details - Fourth */}
                  {selectedEvidence.supporting_details?.quotes && selectedEvidence.supporting_details.quotes.length > 0 && (() => {
                    // Filter out the key quote to avoid duplication
                    const otherQuotes = selectedEvidence.supporting_details.quotes.filter(
                      quote => quote !== selectedEvidence.source_reference?.key_quote
                    );
                    
                    if (otherQuotes.length === 0) return null;
                    
                    return (
                      <div className="mb-3">
                        <span className="text-sm text-gray-500">Other Quotes: </span>
                        <div className="mt-1 space-y-1">
                          {otherQuotes.map((quote, idx) => (
                            <p key={idx} className="text-gray-700 italic bg-gray-50 p-2 rounded border-l-4 border-gray-300">
                              "{quote}"
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Metrics - Fifth */}
                  {selectedEvidence.supporting_details?.metrics && selectedEvidence.supporting_details.metrics.length > 0 && (
                    <div className="mb-3">
                      <span className="text-sm text-gray-500">Metrics Cited: </span>
                      <ul className="mt-1 list-disc list-inside text-sm text-gray-700">
                        {selectedEvidence.supporting_details.metrics.map((metric, idx) => (
                          <li key={idx}>{metric}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Timeline */}
                  {selectedEvidence.supporting_details?.timeline && (
                    <div className="mb-3">
                      <span className="text-sm text-gray-500">Timeline: </span>
                      <span className="text-sm text-gray-700">{selectedEvidence.supporting_details.timeline}</span>
                    </div>
                  )}
                  
                  {/* Conditions */}
                  {selectedEvidence.supporting_details?.conditions && (
                    <div className="mb-3">
                      <span className="text-sm text-gray-500">Conditions: </span>
                      <span className="text-sm text-gray-700">{selectedEvidence.supporting_details.conditions}</span>
                    </div>
                  )}
                  
                  {/* Related Initiatives - Last */}
                  {selectedEvidence.supporting_details?.related_initiatives && selectedEvidence.supporting_details.related_initiatives.length > 0 && (
                    <div>
                      <span className="text-sm text-gray-500">Related Initiatives: </span>
                      <ul className="mt-1 list-disc list-inside text-sm text-gray-700">
                        {selectedEvidence.supporting_details.related_initiatives.map((initiative, idx) => (
                          <li key={idx}>{initiative}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          


          {/* Data Quality & Clarifications */}
          {(selectedEvidence.data_gaps || selectedEvidence.requires_clarification) && (
            <div className="mb-6 border border-gray-200 rounded-lg">
              <button
                onClick={() => setIsDataQualityExpanded(!isDataQualityExpanded)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h4 className="font-semibold text-gray-700">Data Quality & Clarifications</h4>
                <svg 
                  className={`h-5 w-5 text-gray-500 transform transition-transform ${isDataQualityExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isDataQualityExpanded && (
                <div className="px-4 pb-4">
                  {/* Data Gaps Section */}
                  {selectedEvidence.data_gaps && (
                    <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                      <h5 className="font-medium text-gray-700 mb-2">Data Gaps</h5>
                      {selectedEvidence.data_gaps.critical_missing && selectedEvidence.data_gaps.critical_missing.length > 0 && (
                        <div className="mb-2">
                          <span className="text-sm text-gray-500 font-medium">Critical Missing Information:</span>
                          <ul className="mt-1 list-disc list-inside">
                            {selectedEvidence.data_gaps.critical_missing.map((item, idx) => (
                              <li key={idx} className="text-sm text-gray-700">{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {selectedEvidence.data_gaps.affects_confidence !== undefined && (
                        <div>
                          <span className="text-sm text-gray-500 font-medium">Affects Confidence:</span>
                          <span className="text-sm text-gray-700 ml-2">
                            {selectedEvidence.data_gaps.affects_confidence ? 'Yes' : 'No'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Requires Clarification Section */}
                  {selectedEvidence.requires_clarification && (
                    <div className="p-3 bg-orange-50 rounded-lg">
                      <h5 className="font-medium text-gray-700 mb-2">Requires Clarification</h5>
                      {selectedEvidence.requires_clarification.ambiguous_elements && selectedEvidence.requires_clarification.ambiguous_elements.length > 0 && (
                        <div className="mb-2">
                          <span className="text-sm text-gray-500 font-medium">Ambiguous Elements:</span>
                          <ul className="mt-1 list-disc list-inside">
                            {selectedEvidence.requires_clarification.ambiguous_elements.map((item, idx) => (
                              <li key={idx} className="text-sm text-gray-700">{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {selectedEvidence.requires_clarification.verification_needed !== undefined && (
                        <div>
                          <span className="text-sm text-gray-500 font-medium">Verification Needed:</span>
                          <span className="text-sm text-gray-700 ml-2">
                            {selectedEvidence.requires_clarification.verification_needed ? 'Yes' : 'No'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}


          
        </div>
        
        {/* Modal footer */}
        <div className="px-6 py-4 border-t flex justify-between bg-gray-50 rounded-b-lg">
          <button
            onClick={handlePrevious}
            disabled={!hasPrevious}
            className="px-3 py-1.5 rounded border bg-white text-gray-700 disabled:opacity-40 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <div className="text-gray-500 text-sm flex items-center">
            Item {currentIndex + 1} of {filteredEvidence.length}
          </div>
          <button
            onClick={handleNext}
            disabled={!hasNext}
            className="px-3 py-1.5 rounded border bg-white text-gray-700 disabled:opacity-40 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}; 